import React, { useState } from 'react';
import {
  BUSINESS_PROFILES,
  DEFAULT_BUSINESS_PROFILE,
  getBusinessProfile
} from '../businessProfiles';
import { createTrialAccount } from '../services/erpSessionClient';
import { getStoredAffiliateCode, normalizeAffiliateCode } from '../services/partnerApiClient';
import '../styles/Login.css';

const NIVORA_LOGO = `${process.env.PUBLIC_URL || ''}/assets/nivora-logo.png`;

export default function Signup() {
  const [form, setForm] = useState({
    tenantSlug: '',
    tenantName: '',
    businessProfile: DEFAULT_BUSINESS_PROFILE,
    ownerUsername: '',
    ownerDisplayName: '',
    ownerPassword: '',
    referralCode: getStoredAffiliateCode()
  });
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const update = (field) => (event) => setForm((current) => ({
    ...current,
    [field]: event.target.value
  }));
  const selectedProfile = getBusinessProfile(form.businessProfile);

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if (form.ownerPassword.length < 10) {
      setError('Para criar a conta, a senha precisa ter no mínimo 10 caracteres. O cadastro não foi enviado.');
      return;
    }
    setLoading(true);
    try {
      setResult(await createTrialAccount({
        ...form,
        tenantDisplayName: form.tenantName
      }));
    } catch (signupError) {
      setError(signupError?.message || 'Não foi possível criar sua conta.');
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    const createdProfile = getBusinessProfile(result.tenant.businessProfile);
    return (
      <div className="login-container">
        <div className="login-box">
          <div className="login-header">
            <img className="login-product-logo" src={NIVORA_LOGO} alt="Nivora ERP" />
            <h1>Conta criada</h1>
            <p>Seu teste de 30 dias já está pronto.</p>
          </div>
          <div className="login-security-note">
            <strong>Empresa:</strong> {result.tenant.displayName}<br />
            <strong>Identificador:</strong> {result.tenant.slug}<br />
            <strong>Segmento:</strong> {createdProfile.label}<br />
            <strong>Usuário:</strong> {result.owner.username}
            {result.referral && <><br /><strong>Representante:</strong> {result.referral.affiliateName} ({result.referral.code})</>}
          </div>
          <a className="btn btn-primary btn-lg signup-submit" href="/login">Ir para o login</a>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-box signup-box">
        <div className="login-header">
          <img className="login-product-logo" src={NIVORA_LOGO} alt="Nivora ERP" />
          <h1>Comece agora</h1>
          <p>Crie sua empresa e teste por 30 dias.</p>
        </div>
        {error && <div className="alert alert-danger" role="alert">{error}</div>}
        <form onSubmit={submit}>
          <div className="form-group">
            <label className="form-label" htmlFor="signup-tenant-name">Nome da empresa</label>
            <input
              id="signup-tenant-name"
              className="form-control"
              value={form.tenantName}
              onChange={update('tenantName')}
              maxLength="180"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="signup-business-profile">Segmento da empresa</label>
            <select
              id="signup-business-profile"
              className="form-control"
              value={form.businessProfile}
              onChange={update('businessProfile')}
              aria-describedby="signup-business-profile-help"
              required
            >
              {BUSINESS_PROFILES.map((profile) => (
                <option key={profile.code} value={profile.code}>{profile.signupLabel}</option>
              ))}
            </select>
            <small id="signup-business-profile-help" className="login-field-help">
              {selectedProfile.description}
            </small>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="signup-tenant-slug">Identificador da empresa</label>
            <input
              id="signup-tenant-slug"
              className="form-control"
              value={form.tenantSlug}
              onChange={update('tenantSlug')}
              pattern="[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*"
              maxLength="80"
              placeholder="ex.: minha-empresa"
              required
            />
            <small className="login-field-help">Use este identificador também no login.</small>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="signup-referral-code">Código do representante · opcional</label>
            <input
              id="signup-referral-code"
              className="form-control"
              value={form.referralCode}
              onChange={(event) => setForm((current) => ({
                ...current,
                referralCode: event.target.value.toUpperCase()
              }))}
              onBlur={() => setForm((current) => ({
                ...current,
                referralCode: normalizeAffiliateCode(current.referralCode)
                  || current.referralCode.trim().toUpperCase()
              }))}
              maxLength="40"
              placeholder="Ex.: NIVORA-MARIA-A1B2C3"
              autoCapitalize="characters"
            />
            <small className="login-field-help">
              Preenchido automaticamente quando você acessa pelo link de um representante Nivora.
            </small>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="signup-owner-name">Seu nome</label>
            <input
              id="signup-owner-name"
              className="form-control"
              value={form.ownerDisplayName}
              onChange={update('ownerDisplayName')}
              maxLength="160"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="signup-owner-email">E-mail de acesso</label>
            <input
              id="signup-owner-email"
              type="email"
              className="form-control"
              value={form.ownerUsername}
              onChange={update('ownerUsername')}
              autoComplete="email"
              maxLength="64"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="signup-owner-password">Senha</label>
            <input
              id="signup-owner-password"
              type="password"
              className="form-control"
              value={form.ownerPassword}
              onChange={update('ownerPassword')}
              autoComplete="new-password"
              aria-describedby="signup-owner-password-help"
              minLength="10"
              maxLength="200"
              required
            />
            <div
              id="signup-owner-password-help"
              className={`signup-password-requirement ${form.ownerPassword.length >= 10 ? 'is-valid' : ''}`}
              role="status"
              aria-live="polite"
            >
              <strong>Obrigatório: mínimo de 10 caracteres.</strong>
              <span>{form.ownerPassword.length}/10 caracteres preenchidos</span>
            </div>
            <small className="login-field-help">O cadastro só será enviado quando esta exigência for atendida.</small>
          </div>
          <button
            type="submit"
            className="btn btn-primary btn-lg signup-submit"
            disabled={loading}
          >
            {loading ? 'Criando conta...' : 'Criar minha conta grátis'}
          </button>
        </form>
        <p className="login-help"><a href="/login">Já tenho uma conta</a></p>
      </div>
    </div>
  );
}
